import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzRadioModule} from 'ng-zorro-antd/radio';
import {NzSelectModule} from 'ng-zorro-antd/select';
import {TranslocoPipe} from '@jsverse/transloco';
import type {Factor} from '@supabase/supabase-js';
import {AccountApiService} from '../../services/account-api.service';
import {SupabaseAuthService} from '../../services/supabase-auth.service';

export interface SecurityVerificationDialogData {
  action: string;
  resource: Record<string, unknown>;
}

export interface SecurityVerificationResult {
  ticket: string;
  expires_at: string;
  method: 'email_otp' | 'mfa';
}

@Component({
  selector: 'app-security-verification-dialog',
  imports: [
    FormsModule,
    NzAlertModule,
    NzButtonModule,
    NzInputModule,
    NzRadioModule,
    NzSelectModule,
    TranslocoPipe,
  ],
  templateUrl: './security-verification-dialog.component.html',
  styleUrl: './security-verification-dialog.component.scss',
})
export class SecurityVerificationDialogComponent implements OnInit {
  readonly method = signal<'email_otp' | 'mfa'>('email_otp');
  readonly email = signal('');
  readonly emailCode = signal('');
  readonly emailCooldown = signal(0);
  readonly emailSending = signal(false);
  readonly factors = signal<Factor<'totp', 'verified'>[]>([]);
  readonly selectedFactorId = signal('');
  readonly mfaCode = signal('');
  readonly loading = signal(false);
  readonly error = signal('');

  private readonly data = inject<SecurityVerificationDialogData>(NZ_MODAL_DATA);
  readonly modalRef = inject(NzModalRef);
  private readonly accountApi = inject(AccountApiService);
  private readonly auth = inject(SupabaseAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private emailCooldownTimer: ReturnType<typeof setInterval> | null = null;

  async ngOnInit() {
    this.destroyRef.onDestroy(() => this.clearEmailCooldown());
    const [session, factors] = await Promise.all([
      this.auth.getSession(),
      this.auth.listTotpFactors().catch(() => [] as Factor<'totp', 'verified'>[]),
    ]);
    this.email.set(session?.user?.email ?? '');
    this.factors.set(factors);
    this.selectedFactorId.set(factors[0]?.id ?? '');
  }

  async sendEmailCode() {
    if (this.emailSending() || this.loading() || this.emailCooldown() > 0) {
      return;
    }

    this.emailSending.set(true);
    this.error.set('');
    try {
      await this.accountApi.getCaptcha({
        username: this.email(),
        action: this.data.action,
      });
      this.startEmailCooldown(60);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : '验证码发送失败');
    } finally {
      this.emailSending.set(false);
    }
  }

  async verify() {
    this.loading.set(true);
    this.error.set('');
    try {
      const method = this.method();
      if (method === 'mfa') {
        await this.auth.challengeAndVerifyTotp(this.selectedFactorId(), this.mfaCode());
      }
      const issued = await this.accountApi.issueSecurityVerification({
        action: this.data.action,
        resource: this.data.resource,
        method,
        ...(method === 'email_otp' ? {code: this.emailCode()} : {}),
      });
      this.modalRef.close({
        ...issued,
        method,
      } satisfies SecurityVerificationResult);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : '安全验证失败');
    } finally {
      this.loading.set(false);
    }
  }

  private startEmailCooldown(seconds: number): void {
    this.clearEmailCooldown();
    this.emailCooldown.set(seconds);
    this.emailCooldownTimer = setInterval(() => {
      const next = this.emailCooldown() - 1;
      if (next <= 0) {
        this.clearEmailCooldown();
        return;
      }
      this.emailCooldown.set(next);
    }, 1000);
  }

  private clearEmailCooldown(): void {
    if (this.emailCooldownTimer) {
      clearInterval(this.emailCooldownTimer);
      this.emailCooldownTimer = null;
    }
    this.emailCooldown.set(0);
  }
}
